import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Calendar, Clock, MapPin, Users, Trash2, Lock, Unlock, Loader2,
  ListOrdered, CheckCircle, UserMinus, ArrowUpCircle, Timer, CreditCard, Pencil, Shuffle, AlertTriangle,
} from "lucide-react";

function PlayerRow({ player, index, isPaid, deadlineRp, isExpiring, hoursLeft, onRemove, t }) {
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg ${
        isPaid ? "bg-green-500/10" : isExpiring ? "bg-amber-500/10 border border-amber-500/20" : "bg-green-500/10"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
          isPaid ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"
        }`}>
          {index + 1}
        </div>
        {player.photo_url && <img src={player.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />}
        <div>
          <p className="font-medium">{player.player_name || player.name}</p>
          <div className="flex items-center gap-2 flex-wrap">
            {player.position && <span className="text-xs text-muted-foreground">{player.position}</span>}
            <Badge className={`text-[10px] px-1.5 py-0 ${
              (player.member_type || "membro") === "membro"
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                : "bg-amber-500/20 text-amber-400 border-amber-500/30"
            }`}>
              {(player.member_type || "membro") === "membro" ? t("gameRegistrations.member") : t("gameRegistrations.guest")}
            </Badge>
            {isPaid ? (
              <Badge className="text-[10px] px-1.5 py-0 bg-green-500/20 text-green-400 border-green-500/30">
                {t("gameRegistrations.paid")}
              </Badge>
            ) : deadlineRp ? (
              <Badge className="text-[10px] px-1.5 py-0 bg-amber-500/20 text-amber-400 border-amber-500/30 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {hoursLeft !== null && hoursLeft > 0 ? `${hoursLeft}${t("gameRegistrations.hoursLeft")}` : t("gameRegistrations.expiring")}
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="text-destructive"
        onClick={() => onRemove(player.player_id)}
        data-testid={`remove-player-${player.player_id}`}
      >
        <UserMinus className="h-4 w-4" />
      </Button>
    </div>
  );
}

function WaitlistRow({ player, index, onRemove, t }) {
  return (
    <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold text-sm">
          {index + 1}
        </div>
        {player.photo_url && <img src={player.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />}
        <div>
          <p className="font-medium">{player.player_name || player.name}</p>
          <div className="flex items-center gap-2">
            {player.position && <span className="text-xs text-muted-foreground">{player.position}</span>}
            <Badge className={`text-[10px] px-1.5 py-0 ${
              (player.member_type || "membro") === "membro"
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                : "bg-amber-500/20 text-amber-400 border-amber-500/30"
            }`}>
              {(player.member_type || "membro") === "membro" ? t("gameRegistrations.member") : t("gameRegistrations.guest")}
            </Badge>
          </div>
        </div>
      </div>
      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => onRemove(player.player_id)}>
        <UserMinus className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function GameDetailPanel({
  selectedGame,
  registeredPlayers,
  waitlistPlayers,
  maxPlayers,
  spotsAvailable,
  paymentSummary,
  deadlineStr,
  deadlinePassed,
  isPromoting,
  handleToggleStatus,
  handleDeleteGame,
  handleRemovePlayer,
  handlePromoteWaitlist,
  openEditDialog,
  t,
}) {
  const navigate = useNavigate();

  if (!selectedGame) {
    return (
      <Card className="bg-card/50 border-border/50">
        <CardContent className="p-8 text-center text-muted-foreground">
          <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg">{t("gameRegistrations.selectGame")}</p>
          <p className="text-sm">Ou abra novas inscricoes</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 border-border/50">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{selectedGame.title}</CardTitle>
            <CardDescription className="flex flex-wrap items-center gap-4 mt-2">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(selectedGame.date + "T00:00:00").toLocaleDateString("pt-BR")}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {selectedGame.time}
              </span>
              {selectedGame.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {selectedGame.location}
                </span>
              )}
            </CardDescription>
            {deadlineStr && (
              <div className={`flex items-center gap-2 mt-2 text-sm ${deadlinePassed ? "text-green-400" : "text-amber-400"}`}>
                <Timer className="h-4 w-4" />
                <span>
                  Prazo convidados: {new Date(deadlineStr).toLocaleString("pt-BR")}
                  {deadlinePassed ? " (expirado - vagas abertas para todos)" : " (somente membros)"}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {registeredPlayers.length >= 2 && (
              <Button
                size="sm"
                onClick={() => navigate(`/gameday?from_game=${selectedGame.id}`)}
                className="bg-[hsl(var(--highlight))] text-[hsl(var(--highlight-foreground))] hover:opacity-90"
                data-testid="generate-teams-btn"
              >
                <Shuffle className="h-4 w-4 mr-1" />
                {t("gameRegistrations.generateTeams")}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => openEditDialog(selectedGame)} data-testid="edit-game-btn">
              <Pencil className="h-4 w-4 mr-1" />
              Editar
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleToggleStatus(selectedGame.id, selectedGame.is_open)}>
              {selectedGame.is_open ? (
                <><Lock className="h-4 w-4 mr-1" /> Fechar</>
              ) : (
                <><Unlock className="h-4 w-4 mr-1" /> Reabrir</>
              )}
            </Button>
            <Button variant="destructive" size="sm" onClick={() => handleDeleteGame(selectedGame.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Payment Summary */}
        {paymentSummary && (
          <div className="mb-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-green-400" />
              <span className="text-sm font-medium text-green-400">
                Pagamentos: {paymentSummary.paid_count || 0}/{registeredPlayers.length}
              </span>
            </div>
            <span className="text-sm font-bold text-green-400">R$ {(paymentSummary.total_collected || 0).toFixed(2)}</span>
          </div>
        )}

        {/* Registered Players */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            {t("gameRegistrations.registeredPlayers")} ({registeredPlayers.length}/{maxPlayers})
            {spotsAvailable > 0 && (
              <span className="text-xs text-muted-foreground font-normal">
                ({spotsAvailable} {spotsAvailable === 1 ? "vaga" : "vagas"})
              </span>
            )}
          </h3>
          {registeredPlayers.length > 0 ? (
            <div className="grid gap-2">
              {registeredPlayers.map((rp, index) => {
                const isPaid = paymentSummary?.paid_player_ids?.includes(rp.player_id);
                const deadlineRp = rp.payment_deadline ? new Date(rp.payment_deadline) : null;
                const isExpiring = deadlineRp && !isPaid && deadlineRp > new Date();
                const hoursLeft = deadlineRp && !isPaid ? Math.max(0, Math.round((deadlineRp - new Date()) / 3600000)) : null;
                return (
                  <PlayerRow
                    key={rp.player_id || index}
                    player={rp}
                    index={index}
                    isPaid={isPaid}
                    deadlineRp={deadlineRp}
                    isExpiring={isExpiring}
                    hoursLeft={hoursLeft}
                    onRemove={(pid) => handleRemovePlayer(selectedGame.id, pid)}
                    t={t}
                  />
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">{t("gameRegistrations.noPlayersRegistered")}</p>
          )}
        </div>

        {/* Waitlist */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <ListOrdered className="h-4 w-4 text-amber-500" />
              {t("gameRegistrations.waitlistTitle")} ({waitlistPlayers.length})
            </h3>
            {waitlistPlayers.length > 0 && spotsAvailable > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePromoteWaitlist(selectedGame.id)}
                disabled={isPromoting}
                className="text-green-400 border-green-500/30 hover:bg-green-500/10"
                data-testid="promote-waitlist-btn"
              >
                {isPromoting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ArrowUpCircle className="h-4 w-4 mr-1" />}
                {t("gameRegistrations.promoteSpots")}
              </Button>
            )}
          </div>
          {waitlistPlayers.length > 0 ? (
            <div className="grid gap-2">
              {waitlistPlayers.map((wp, index) => (
                <WaitlistRow
                  key={wp.player_id || index}
                  player={wp}
                  index={index}
                  onRemove={(pid) => handleRemovePlayer(selectedGame.id, pid)}
                  t={t}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">{t("gameRegistrations.emptyWaitlist")}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
